import React, { useEffect } from 'react';
import { Card, Form, Button, Tabs, Input, message, Space, Switch, Table, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

const FooterSettings: React.FC = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const [isLinkModalVisible, setIsLinkModalVisible] = React.useState(false);
    const [editingLink, setEditingLink] = React.useState<any>(null);
    const [linkForm] = Form.useForm();

    const { data: footerData } = useQuery({
        queryKey: ['adminFooterConfig'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/footer');
            return response.data.data;
        }
    });

    useEffect(() => {
        if (footerData) {
            form.setFieldsValue({
                logoUrl: footerData.logoUrl,
                facebookUrl: footerData.facebookUrl,
                instagramUrl: footerData.instagramUrl,
                twitterUrl: footerData.twitterUrl,
                youtubeUrl: footerData.youtubeUrl,
                translations: footerData.translations,
                links: footerData.links || []
            });
        }
    }, [footerData, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            const response = await apiClient.put('/admin/footer', values);
            return response.data;
        },
        onSuccess: () => {
            message.success('Footer Einstellungen erfolgreich gespeichert');
            queryClient.invalidateQueries({ queryKey: ['adminFooterConfig'] });
        },
        onError: () => message.error('Fehler beim Speichern')
    });

    const getLanguageLabel = (lang: string) => {
        if (lang === 'de') return 'Deutsch';
        if (lang === 'tr') return 'Türkçe';
        if (lang === 'en') return 'English';
        return lang.toUpperCase();
    };

    const handleEditLink = (link: any) => {
        setEditingLink(link);
        linkForm.setFieldsValue(link);
        setIsLinkModalVisible(true);
    };

    const handleSaveLink = (values: any) => {
        const currentLinks = form.getFieldValue('links') || [];
        if (editingLink) {
            const updatedLinks = currentLinks.map((l: any) => l.id === editingLink.id ? { ...l, ...values } : l);
            form.setFieldValue('links', updatedLinks);
        } else {
            const newLink = { ...values, id: crypto.randomUUID(), orderIndex: currentLinks.length + 1 };
            form.setFieldValue('links', [...currentLinks, newLink]);
        }
        setIsLinkModalVisible(false);
        setEditingLink(null);
        linkForm.resetFields();
    };

    const handleDeleteLink = (id: string) => {
        const currentLinks = form.getFieldValue('links') || [];
        form.setFieldValue('links', currentLinks.filter((l: any) => l.id !== id));
    };

    const linkColumns = [
        {
            title: 'Titel (DE)',
            dataIndex: ['translations', 'de', 'title'],
            key: 'title_de',
        },
        {
            title: 'URL',
            dataIndex: ['translations', 'de', 'url'],
            key: 'url',
        },
        {
            title: 'Aktiv',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (val: boolean) => <Switch checked={val} disabled />
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEditLink(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteLink(record.id)} />
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: getLanguageLabel(lang),
        children: (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Form.Item name={['translations', lang, 'logoSubtitle']} label="Logo Untertitel">
                    <Input />
                </Form.Item>
                <Form.Item name={['translations', lang, 'infoTitle']} label="Info Spalte Titel">
                    <Input />
                </Form.Item>
                <Form.Item name={['translations', lang, 'copyrightText']} label="Copyright Text" className="md:col-span-2">
                    <Input.TextArea rows={2} />
                </Form.Item>
            </div>
        )
    }));

    return (
        <div className="p-6">
            <Card title="Footer Einstellungen">
                <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
                    <Tabs defaultActiveKey="global" items={[
                        {
                            key: 'global',
                            label: 'Global Settings',
                            children: (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Form.Item name="logoUrl" label="Logo URL">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="facebookUrl" label="Facebook URL">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="instagramUrl" label="Instagram URL">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="twitterUrl" label="Twitter URL">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="youtubeUrl" label="Youtube URL">
                                        <Input />
                                    </Form.Item>
                                </div>
                            )
                        },
                        {
                            key: 'content',
                            label: 'Localized Content',
                            children: <Tabs items={tabItems} type="card" />
                        },
                        {
                            key: 'links',
                            label: 'Navigation Links',
                            children: (
                                <>
                                    <div className="mb-4 flex justify-end">
                                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingLink(null); linkForm.resetFields(); setIsLinkModalVisible(true); }}>
                                            Link hinzufügen
                                        </Button>
                                    </div>
                                    <Form.Item name="links" valuePropName="dataSource" noStyle>
                                        <Table columns={linkColumns} rowKey="id" pagination={false} />
                                    </Form.Item>
                                </>
                            )
                        }
                    ]} />

                    <div className="mt-8">
                        <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending}>
                            Speichern
                        </Button>
                    </div>
                </Form>
            </Card>

            <Modal
                title={editingLink ? "Link bearbeiten" : "Link hinzufügen"}
                open={isLinkModalVisible}
                onCancel={() => setIsLinkModalVisible(false)}
                onOk={() => linkForm.submit()}
                width={800}
            >
                <Form form={linkForm} layout="vertical" onFinish={handleSaveLink}>
                    <Form.Item name="isActive" label="Aktiv" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Tabs items={appLanguages.map(lang => ({
                        key: lang,
                        label: getLanguageLabel(lang),
                        children: (
                            <div className="grid grid-cols-1 gap-4">
                                <Form.Item name={['translations', lang, 'title']} label="Titel" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item name={['translations', lang, 'url']} label="URL" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </div>
                        )
                    }))} type="card" />
                </Form>
            </Modal>
        </div>
    );
};

export default FooterSettings;
